'use client';

import { useState, useRef, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Language {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
}

interface LanguageVoiceToggleProps {
  onLanguageChange?: (language: Language) => void;
  onVoiceToggle?: (isActive: boolean) => void;
}

const LanguageVoiceToggle = ({ onLanguageChange, onVoiceToggle }: LanguageVoiceToggleProps) => {
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<Language>({
    code: 'en',
    name: 'English',
    nativeName: 'English',
    flag: '🇺🇸',
  });
  const [isVoiceActive, setIsVoiceActive] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const languages: Language[] = [
    { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
    { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
    { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
    { code: 'zh', name: 'Chinese', nativeName: '中文', flag: '🇨🇳' },
    { code: 'ja', name: 'Japanese', nativeName: '日本語', flag: '🇯🇵' },
  ];

  const handleLanguageSelect = (language: Language) => {
    setSelectedLanguage(language);
    setIsLanguageOpen(false);
    onLanguageChange?.(language);
  };

  const handleVoiceToggle = () => {
    const newState = !isVoiceActive;
    setIsVoiceActive(newState);
    onVoiceToggle?.(newState);

    if (newState) {
      setIsListening(true);
      setTimeout(() => setIsListening(false), 3000);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsLanguageOpen(false);
      }
    };

    if (isLanguageOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isLanguageOpen]);

  return (
    <div className="flex items-center gap-2">
      {/* Language Selector */}
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsLanguageOpen(!isLanguageOpen)}
          className="flex items-center gap-2 px-3 py-2 bg-card border border-border rounded-md transition-smooth hover:bg-muted hover:shadow-elevation-sm focus-ring"
          aria-label="Select language"
          aria-expanded={isLanguageOpen}
        >
          <span className="text-lg leading-none">{selectedLanguage.flag}</span>
          <span className="text-sm font-medium text-foreground hidden md:inline">
            {selectedLanguage.code.toUpperCase()}
          </span>
          <Icon 
            name="ChevronDownIcon" 
            size={14} 
            className={`text-muted-foreground transition-smooth ${isLanguageOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {isLanguageOpen && (
          <div className="absolute top-full right-0 mt-2 w-64 bg-popover border border-border rounded-md shadow-elevation-lg animate-slide-down z-[110]">
            <div className="p-2">
              {languages.map((language) => (
                <button
                  key={language.code}
                  onClick={() => handleLanguageSelect(language)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-md transition-smooth hover:bg-muted ${
                    selectedLanguage.code === language.code ? 'bg-muted' : ''
                  }`}
                >
                  <span className="text-lg leading-none">{language.flag}</span>
                  <div className="flex-1 text-left">
                    <div className="text-sm font-medium text-foreground">{language.name}</div>
                    <div className="text-xs text-muted-foreground">{language.nativeName}</div>
                  </div>
                  {selectedLanguage.code === language.code && (
                    <Icon name="CheckIcon" size={16} className="text-primary" />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Voice Toggle */}
      <button
        onClick={handleVoiceToggle}
        className={`relative p-2 rounded-md transition-smooth focus-ring ${
          isVoiceActive
            ? 'bg-primary text-primary-foreground shadow-elevation-sm'
            : 'bg-card border border-border hover:bg-muted hover:shadow-elevation-sm'
        }`}
        aria-label={isVoiceActive ? 'Disable voice assistant' : 'Enable voice assistant'}
      >
        <Icon 
          name="MicrophoneIcon" 
          size={20} 
          className={isListening ? 'animate-pulse-subtle' : ''}
        />
        {isListening && (
          <span className="absolute -top-1 -right-1 flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-error opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-error"></span>
          </span>
        )}
      </button>
    </div>
  );
};

export default LanguageVoiceToggle;